import { Badge, Empty, Icon, LinkWithPermission, PopOver } from '@common-web';
import { NOTIFICATION_PATHS_PERMISSIONS } from '@constants/generic-constants.js';
import { ACTION_MARK_READ, PERMISSION_KEY_NOTIFICATION } from '@constants/permission-constants.js';
import { useGetRequest } from '@hooks/index.js';
import { fetchNotificationsListing, markRead } from '@reducers/seller-center/notifications-slice';
import { Flex, Image, Spin } from 'antd';
import cx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { markReadApi } from '../../../../apis/seller-center/notifications';
import placeholderImage from '../../../../assets/placeholder.png';
import PermissionsHelper from '../../../../auth/helper/PermissionHelper.js';
import ConfigDB from '../../../../data/customizer/theme';
import { sanitizeNotificationHTML } from './helper/index';
import styles from './index.module.scss';

dayjs.extend(relativeTime);

const Notifications = () => {
  const [visible, setVisible] = useState(false);
  const { loading, notificationsData, unreadCount, pagination } = useSelector((state) => state.notificationsData);
  const dispatch = useDispatch();
  const { authorized, userInfo } = useSelector((state) => state?.userData ?? {});
  const color = ConfigDB?.data?.color?.secondary_color;

  const fetchNotifications = (params = '') => {
    return dispatch(fetchNotificationsListing(params));
  };

  const checkPathPermission = (deepLink) => {
    const permissionKey = Object.keys(NOTIFICATION_PATHS_PERMISSIONS).find((path) => deepLink.startsWith(path));
    return permissionKey
      ? PermissionsHelper.getPermissions(
          NOTIFICATION_PATHS_PERMISSIONS[permissionKey]?.permission,
          NOTIFICATION_PATHS_PERMISSIONS[permissionKey]?.action
        )
      : true;
  };

  const markNotification = (notification) => {
    if (notification?.unread && PermissionsHelper.getPermissions(PERMISSION_KEY_NOTIFICATION, ACTION_MARK_READ)) {
      dispatch(markRead(notification?.id));
      markReadApi({ notification_ids: [notification?.id] });
    }
  };

  useGetRequest({
    key: ['notifications', userInfo?.userProfile?.id],
    fetch: () => fetchNotifications(),
    enabled: authorized,
  });

  return (
    <PopOver
      title={'Notifications'}
      className={styles.notificationPopOver}
      content={
        loading && !pagination ? (
          <Spin size="small" />
        ) : (
          <InfiniteScroll
            height={500}
            dataLength={notificationsData?.length}
            hasMore={!!pagination?.next_page}
            loader={<></>}
            next={() => {
              if (pagination?.next_page) {
                fetchNotifications(`?page=${pagination?.next_page}`);
              }
            }}
          >
            {notificationsData?.length === 0 ? (
              <Empty style={{ display: 'grid', alignContent: 'center', height: '100%' }} />
            ) : (
              notificationsData?.map((notification) => (
                <div key={notification?.id}>
                  {notification?.label && <div className={styles.noificationDay}>{notification?.label}</div>}
                  <LinkWithPermission
                    className={cx(styles.noificationItem, notification?.unread && styles.unread)}
                    to={notification?.deep_link}
                    authorized={checkPathPermission(notification?.deep_link)}
                  >
                    <Flex
                      onClick={() => {
                        markNotification(notification);
                        setVisible(false);
                      }}
                      align="center"
                      gap={12}
                    >
                      <Flex
                        align="center"
                        justify="center"
                        className={styles.imageOuter}
                        style={{ width: 40, height: 40 }}
                      >
                        <Image
                          src={notification?.icon_url ?? placeholderImage}
                          style={{ width: 40, height: 40 }}
                          preview={false}
                        />
                      </Flex>
                      <Flex vertical flex={1}>
                        <div
                          className={styles.notificationMessage}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeNotificationHTML(
                              notification?.message?.length > 100
                                ? `${notification.message.substring(0, 100)}...`
                                : notification.message
                            ),
                          }}
                        />
                        <div className="fs-12 gray-500">{dayjs(notification?.created_at).fromNow()}</div>
                      </Flex>
                      {notification?.unread && <Badge color={color} />}
                    </Flex>
                  </LinkWithPermission>
                </div>
              ))
            )}
          </InfiniteScroll>
        )
      }
      trigger={'click'}
      visible={visible}
      setVisible={setVisible}
    >
      <Flex align="center">
        <Badge dot={unreadCount > 0} offset={[-6, 8]} status={'error'} className="flex cursor-pointer">
          <Icon icon="IoMdNotificationsOutline" iconSize="24" />
        </Badge>
      </Flex>
    </PopOver>
  );
};

export default Notifications;
